import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";
import { InviteUserDialog } from "@/components/consultant/InviteUserDialog";
import { EventsDialog } from "@/components/consultant/EventsDialog";
import { ReferralsDialog } from "@/components/consultant/ReferralsDialog";
import { BulletinDialog } from "@/components/consultant/BulletinDialog";
import { NewsletterDialog } from "@/components/consultant/NewsletterDialog";
import { ClientProgramsDialog } from "@/components/consultant/ClientProgramsDialog";
import { AdminPanel } from "@/components/consultant/AdminPanel";
import { DashboardStats } from "@/components/consultant/DashboardStats";
import { DashboardHeader } from "@/components/consultant/DashboardHeader";
import { ClientListView } from "@/components/consultant/ClientListView";
import { DashboardBottomDialogs } from "@/components/consultant/DashboardBottomDialogs";
import { ScaleApplicationsPanel } from "@/components/consultant/ScaleApplicationsPanel";
import { RecommendationsPanel } from "@/components/consultant/RecommendationsPanel";
import { ProjectScopePanel } from "@/components/consultant/ProjectScopePanel";
import { ServiceQuotePanel } from "@/components/consultant/ServiceQuotePanel";
import { SponsorManagementPanel } from "@/components/consultant/SponsorManagementPanel";
import { CompanyManagementPanel } from "@/components/consultant/CompanyManagementPanel";
import { InvitationAnalyticsPanel } from "@/components/consultant/InvitationAnalyticsPanel";
import { OnboardingChecklistPanel } from "@/components/consultant/OnboardingChecklistPanel";
import { MilesAgentPanel } from "@/components/consultant/MilesAgentPanel";
import { OutreachPanel } from "@/components/consultant/OutreachPanel";
import ClientProgressPanel from "@/components/consultant/ClientProgressPanel";
import ProgressReportsPanel from "@/components/consultant/ProgressReportsPanel";
import BillingDashboard from "@/components/consultant/BillingDashboard";
import SalesAgentsDashboard from "@/components/consultant/SalesAgentsDashboard";
import PartnershipAgentDashboard from "@/components/consultant/PartnershipAgentDashboard";
import NotificationBell from "@/components/consultant/NotificationBell";
import QuickActionsBar from "@/components/consultant/QuickActionsBar";
import ActivityScoresPanel from "@/components/consultant/ActivityScoresPanel";
import ProposalGenerator from "@/components/consultant/ProposalGenerator";
import AgentManagementPanel from "@/components/consultant/AgentManagementPanel";
import FormationOrdersPanel from "@/components/consultant/FormationOrdersPanel";
import ClientPipeline from "@/components/consultant/ClientPipeline";
import CondensedSidebar from "@/components/consultant/CondensedSidebar";
import { CommunityMembersDialog } from "@/components/consultant/CommunityMembersDialog";
import { CommunityViewDialog } from "@/components/consultant/CommunityViewDialog";
import { AddConsultantDialog, AssignConsultantDialog, DeleteClientDialog, DeleteConsultantDialog } from "@/components/consultant/TeamManagementDialogs";
import { 
  Users, Calendar, Target, Plus, 
  CheckCircle, Video,
  Bell, UserPlus, Send, Loader2, X, Menu, ClipboardCheck
} from "lucide-react";

// Admin email for full system oversight
const ADMIN_EMAIL = "jamilah@mybizconsulting.net";

export const ConsultantDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, checkAuth } = useAuth();
  const [clients, setClients] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [allConsultants, setAllConsultants] = useState([]);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedClientForAssign, setSelectedClientForAssign] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  
  const [showWorkshopDialog, setShowWorkshopDialog] = useState(false);
  const [showMeetingDialog, setShowMeetingDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  
  const [workshopForm, setWorkshopForm] = useState({
    title: "",
    description: "",
    date: "",
    time: "",
    duration_minutes: 60,
    type: "workshop",
    meeting_link: "",
    payment_link: "",
    location: "Online"
  });

  const [meetingForm, setMeetingForm] = useState({
    client_id: "",
    title: "",
    description: "",
    date: "",
    time: "",
    duration_minutes: 60,
    meeting_type: "strategy"
  });

  const [settings, setSettings] = useState({
    meeting_link: user?.meeting_link || "",
    calendar_link: user?.calendar_link || ""
  });

  const [showAddConsultantDialog, setShowAddConsultantDialog] = useState(false);
  const [consultantForm, setConsultantForm] = useState({
    name: "",
    email: "",
    password: ""
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [clientTiers, setClientTiers] = useState({});
  
  // Add Client state
  const [showAddClientDialog, setShowAddClientDialog] = useState(false);
  const [addClientForm, setAddClientForm] = useState({
    name: "",
    email: "",
    tier: "basic",
    invite_type: "email",  // email, intake
    intake_message: ""
  });
  const [sendingInvite, setSendingInvite] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [pendingClients, setPendingClients] = useState([]);
  const [approvingClient, setApprovingClient] = useState(null);
  const [showDeleteClientDialog, setShowDeleteClientDialog] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [showDeleteConsultantDialog, setShowDeleteConsultantDialog] = useState(false);
  const [consultantToDelete, setConsultantToDelete] = useState(null);
  const [processingAction, setProcessingAction] = useState(null);
  const [upgradeRequests, setUpgradeRequests] = useState([]);
  const [processingUpgrade, setProcessingUpgrade] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(null);
  
  // Client Programs Dialog state
  const [showProgramsDialog, setShowProgramsDialog] = useState(false);
  const [selectedClientForPrograms, setSelectedClientForPrograms] = useState(null);
  
  // Pending Intakes state
  const [pendingIntakes, setPendingIntakes] = useState([]);
  const [processingIntake, setProcessingIntake] = useState(null);

  // Bulletin Board state
  const [bulletinPosts, setBulletinPosts] = useState([]);
  const [showBulletinDialog, setShowBulletinDialog] = useState(false);
  const [bulletinForm, setBulletinForm] = useState({
    title: "",
    content: "",
    category: "announcement",
    link_url: "",
    link_text: "",
    scheduled_at: ""
  });
  const [savingBulletin, setSavingBulletin] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [generatingBulletin, setGeneratingBulletin] = useState(false);
  const [autoBulletinSettings, setAutoBulletinSettings] = useState({
    enabled: false,
    time: "08:00",
    categories: ["tip", "news", "motivation", "resource"],
    days: ["monday", "tuesday", "wednesday", "thursday", "friday"]
  });
  const [generatingAutoBulletin, setGeneratingAutoBulletin] = useState(false);

  // Community Members state
  const [communityMembers, setCommunityMembers] = useState({ members: [], stats: {} });
  const [showCommunityDialog, setShowCommunityDialog] = useState(false);
  const [upgradingMember, setUpgradingMember] = useState(null);

  // Newsletter state
  const [showNewsletterDialog, setShowNewsletterDialog] = useState(false);
  const [newsletterContent, setNewsletterContent] = useState({ subject: "", content: "" });
  const [generatingNewsletter, setGeneratingNewsletter] = useState(false);
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const [newsletters, setNewsletters] = useState([]);
  const [newsletterSettings, setNewsletterSettings] = useState({
    auto_send_enabled: false,
    send_day: "monday",
    send_time: "09:00",
    default_topics: [],
    include_workshops: true
  });

  // Community View state
  const [showCommunityView, setShowCommunityView] = useState(false);
  const [pendingComments, setPendingComments] = useState([]);
  const [postComments, setPostComments] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  const [expandedPosts, setExpandedPosts] = useState({});
  const [newComment, setNewComment] = useState({});
  const [submittingComment, setSubmittingComment] = useState(false);

  // Pending recommendations count for badge
  const [pendingRecommendationsCount, setPendingRecommendationsCount] = useState(0);

  // Referral Management state
  const [showReferralsDialog, setShowReferralsDialog] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [referralStats, setReferralStats] = useState({ total: 0, pending: 0, approved: 0 });
  const [processingReferral, setProcessingReferral] = useState(null);

  // Invite state
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: "", name: "", invite_type: "email", role: "community_member", tier: "basic", send_intake: false, intake_message: ""
  });
  const [sendingIntake, setSendingIntake] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [createdUserCredentials, setCreatedUserCredentials] = useState(null);

  // Events state
  const [showEventsDialog, setShowEventsDialog] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventForm, setEventForm] = useState({
    title: "", description: "", date: "", time: "", duration: "60",
    location: "Online", meeting_link: "", payment_link: "", price: 0, max_attendees: 15
  });
  const [editingEvent, setEditingEvent] = useState(null);
  const [savingEvent, setSavingEvent] = useState(false);
  const [selectedEventAttendees, setSelectedEventAttendees] = useState(null);
  const [reminderMessage, setReminderMessage] = useState("");

  // Cases/CRM state
  const [showCasesView, setShowCasesView] = useState(false);
  const [showLeadsPanel, setShowLeadsPanel] = useState(false);
  const [showScaleApplications, setShowScaleApplications] = useState(false);
  const [showSponsorshipPanel, setShowSponsorshipPanel] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [showProjectScopes, setShowProjectScopes] = useState(false);
  const [showServiceQuotes, setShowServiceQuotes] = useState(false);
  const [showSponsorManagement, setShowSponsorManagement] = useState(false);
  const [showCompanyManagement, setShowCompanyManagement] = useState(false);
  const [showInviteAnalytics, setShowInviteAnalytics] = useState(false);
  const [showOnboardingChecklist, setShowOnboardingChecklist] = useState(false);
  const [showMilesPanel, setShowMilesPanel] = useState(false);
  const [showOutreachPanel, setShowOutreachPanel] = useState(false);
  const [showClientProgress, setShowClientProgress] = useState(false);
  const [showProgressReports, setShowProgressReports] = useState(false);
  const [showBilling, setShowBilling] = useState(false);
  const [showSalesAgents, setShowSalesAgents] = useState(false);
  const [showPartnershipAgent, setShowPartnershipAgent] = useState(false);
  const [showActivityScores, setShowActivityScores] = useState(false);
  const [showProposals, setShowProposals] = useState(false);
  const [showAgentMgmt, setShowAgentMgmt] = useState(false);
  const [showFormationOrders, setShowFormationOrders] = useState(false);
  const [showSecurityPanel, setShowSecurityPanel] = useState(false);
  const [sendIntakeClient, setSendIntakeClient] = useState(null); // For sending intake to existing client
  const [intakeMessage, setIntakeMessage] = useState("");
  const [sendingIntakeToClient, setSendingIntakeToClient] = useState(false);
  
  // View mode: 'pipeline' or 'list'
  const [clientViewMode, setClientViewMode] = useState('list');
  // Assessment leads for pipeline
  const [assessmentLeads, setAssessmentLeads] = useState([]);
  
  const isAdmin = user?.email === ADMIN_EMAIL;

  useEffect(() => {
    fetchData();
    fetchClientTiers();
    fetchPendingInvites();
    fetchPendingIntakes();
    fetchAllConsultants();
    fetchPendingClients();
    fetchUpgradeRequests();
    fetchBulletinPosts();
    fetchCommunityMembers();
    fetchNewsletters();
    fetchNewsletterSettings();
    fetchPendingComments();
    fetchAutoBulletinSettings();
    fetchInvitations();
    fetchEvents();
    fetchReferrals();
    fetchAssessmentLeads();
    fetchPendingRecommendationsCount();
  }, []);

  useEffect(() => {
    setSettings({
      meeting_link: user?.meeting_link || "",
      calendar_link: user?.calendar_link || ""
    });
  }, [user]);

  // Handle quick action navigation from onboarding page
  const [pendingQuickAction, setPendingQuickAction] = useState(null);

  useEffect(() => {
    if (!loading && location.state?.quickAction) {
      setPendingQuickAction(location.state.quickAction);
      // Clear the state so it doesn't re-trigger
      window.history.replaceState({}, document.title);
    }
  }, [loading]);

  const fetchData = async () => {
    try {
      const [clientsRes, workshopsRes, meetingsRes, teamRes] = await Promise.all([
        authFetch(`${API}/consultant/clients`),
        authFetch(`${API}/workshops`),
        authFetch(`${API}/consultant/meetings`),
        authFetch(`${API}/consultant/team`)
      ]);

      
      if (clientsRes.ok) {
        const clientsData = await clientsRes.json();
        setClients(clientsData);
      } else {
        console.error("Failed to fetch clients:", clientsRes.status);
      }
      if (workshopsRes.ok) setWorkshops(await workshopsRes.json());
      if (meetingsRes.ok) setMeetings(await meetingsRes.json());
      if (teamRes.ok) setTeamMembers(await teamRes.json());
    } catch (_error) {
      console.error("Error fetching data:", _error);
      console.error("Error details:", _error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssessmentLeads = async () => {
    try {
      const res = await authFetch(`${API}/assessment/leads?status=new&limit=50`);
      if (res.ok) {
        const data = await res.json();
        setAssessmentLeads(data.leads || []);
      }
    } catch (err) {
      console.error("Failed to fetch assessment leads:", err);
    }
  };

  const fetchClientTiers = async () => {
    try {
      const res = await authFetch(`${API}/client-tiers`, {  });
      if (res.ok) setClientTiers(await res.json());
    } catch (_error) {
      console.error("Error fetching tiers:", _error);
    }
  };

  const fetchPendingInvites = async () => {
    try {
      const res = await authFetch(`${API}/consultant/invitations`, {  });
      if (res.ok) setPendingInvites(await res.json());
    } catch (_error) {
      console.error("Error fetching invites:", _error);
    }
  };

  const fetchPendingIntakes = async () => {
    try {
      const res = await authFetch(`${API}/intake/pending`, {  });
      if (res.ok) setPendingIntakes(await res.json());
    } catch (_error) {
      console.error("Error fetching pending intakes:", _error);
    }
  };

  const handleApproveIntake = async (intakeId) => {
    setProcessingIntake(intakeId);
    try {
      const response = await authFetch(`${API}/intake/${intakeId}/approve`, {
        method: "POST",
        body: JSON.stringify({ tier: "basic" })
      });
      if (response.ok) {
        toast.success("Intake approved - client account activated");
        fetchPendingIntakes();
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to approve intake");
      }
    } catch (_error) {
      toast.error("Failed to approve intake");
    } finally {
      setProcessingIntake(null);
    }
  };

  const handleDenyIntake = async (intakeId) => {
    setProcessingIntake(intakeId);
    try {
      const response = await authFetch(`${API}/intake/${intakeId}/deny`, {
        method: "POST",
        body: JSON.stringify({ reason: "Not approved at this time" })
      });
      if (response.ok) {
        toast.success("Intake denied");
        fetchPendingIntakes();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to deny intake");
      }
    } catch (_error) {
      toast.error("Failed to deny intake");
    } finally {
      setProcessingIntake(null);
    }
  };

  const fetchAllConsultants = async () => {
    try {
      const res = await authFetch(`${API}/consultants`, {  });
      if (res.ok) setAllConsultants(await res.json());
    } catch (_error) {
      console.error("Error fetching consultants:", _error);
    }
  };

  const fetchPendingClients = async () => {
    try {
      const res = await authFetch(`${API}/consultant/pending-clients`, {  });
      if (res.ok) setPendingClients(await res.json());
    } catch (_error) {
      console.error("Error fetching pending clients:", _error);
    }
  };

  const fetchUpgradeRequests = async () => {
    try {
      const res = await authFetch(`${API}/consultant/upgrade-requests`, {  });
      if (res.ok) setUpgradeRequests(await res.json());
    } catch (_error) {
      console.error("Error fetching upgrade requests:", _error);
    }
  };

  const fetchBulletinPosts = async () => {
    try {
      const res = await authFetch(`${API}/bulletin/posts`, {  });
      if (res.ok) setBulletinPosts(await res.json());
    } catch (_error) {
      console.error("Error fetching bulletin posts:", _error);
    }
  };

  const handleCreateBulletinPost = async () => {
    if (!bulletinForm.title || !bulletinForm.content) {
      toast.error("Title and content are required");
      return;
    }
    setSavingBulletin(true);
    try {
      const response = await authFetch(`${API}/bulletin/posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(bulletinForm)
      });
      if (response.ok) {
        toast.success("Bulletin post created!");
        setShowBulletinDialog(false);
        setBulletinForm({ title: "", content: "", category: "announcement", link_url: "", link_text: "" });
        fetchBulletinPosts();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create post");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSavingBulletin(false);
    }
  };

  const handleUpdateBulletinPost = async () => {
    if (!editingPost || !bulletinForm.title || !bulletinForm.content) return;
    setSavingBulletin(true);
    try {
      const response = await authFetch(`${API}/bulletin/posts/${editingPost.post_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(bulletinForm)
      });
      if (response.ok) {
        toast.success("Post updated!");
        setShowBulletinDialog(false);
        setEditingPost(null);
        setBulletinForm({ title: "", content: "", category: "announcement", link_url: "", link_text: "" });
        fetchBulletinPosts();
      } else {
        toast.error("Failed to update post");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSavingBulletin(false);
    }
  };

  const handleDeleteBulletinPost = async (postId) => {
    if (!confirm("Delete this bulletin post?")) return;
    try {
      const response = await authFetch(`${API}/bulletin/posts/${postId}`, {
        method: "DELETE",
        
      });
      if (response.ok) {
        toast.success("Post deleted");
        fetchBulletinPosts();
      } else {
        toast.error("Failed to delete post");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const openEditBulletinDialog = (post) => {
    setEditingPost(post);
    setBulletinForm({
      title: post.title,
      content: post.content,
      category: post.category || "announcement",
      link_url: post.link_url || "",
      link_text: post.link_text || "",
      scheduled_at: post.scheduled_at || ""
    });
    setShowBulletinDialog(true);
  };

  // AI Bulletin Generation
  const handleGenerateAIBulletin = async (category = "tip") => {
    setGeneratingBulletin(true);
    try {
      const response = await authFetch(`${API}/bulletin/posts/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ category })
      });
      if (response.ok) {
        const data = await response.json();
        setBulletinForm(prev => ({
          ...prev,
          title: data.title,
          content: data.content,
          category: data.category
        }));
        toast.success("AI content generated!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to generate content");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setGeneratingBulletin(false);
    }
  };

  // Auto-Bulletin Settings
  const fetchAutoBulletinSettings = async () => {
    try {
      const res = await authFetch(`${API}/bulletin/auto-settings`, {  });
      if (res.ok) setAutoBulletinSettings(await res.json());
    } catch (_error) {
      console.error("Error fetching auto-bulletin settings:", _error);
    }
  };

  const handleSaveAutoBulletinSettings = async () => {
    try {
      const response = await authFetch(`${API}/bulletin/auto-settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(autoBulletinSettings)
      });
      if (response.ok) {
        toast.success("Auto-bulletin settings saved!");
      } else {
        toast.error("Failed to save settings");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const handleTriggerAutoBulletin = async () => {
    setGeneratingAutoBulletin(true);
    try {
      const response = await authFetch(`${API}/bulletin/auto-generate`, {
        method: "POST",
        
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Auto-bulletin created: "${data.title}"`);
        fetchBulletinPosts();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to generate auto-bulletin");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setGeneratingAutoBulletin(false);
    }
  };

  const handlePublishScheduledPost = async (postId) => {
    try {
      const response = await authFetch(`${API}/bulletin/posts/${postId}/publish`, {
        method: "POST",
        
      });
      if (response.ok) {
        toast.success("Post published!");
        fetchBulletinPosts();
      } else {
        toast.error("Failed to publish post");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  // Invite functions
  const fetchInvitations = async () => {
    try {
      const res = await authFetch(`${API}/consultant/invitations`, {  });
      if (res.ok) setInvitations(await res.json());
    } catch (_error) {
      console.error("Error fetching invitations:", _error);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteForm.email) {
      toast.error("Email is required");
      return;
    }
    setSendingInvite(true);
    try {
      
      const response = await authFetch(`${API}/consultant/invite-user`, {
        method: "POST",
        body: JSON.stringify(inviteForm)
      });
      
      
      if (response.ok) {
        const data = await response.json();
        
        // If direct account creation, show the temporary password
        if (data.temp_password) {
          setCreatedUserCredentials({
            email: data.email,
            password: data.temp_password,
            name: inviteForm.name
          });
          setShowCredentialsDialog(true);
        } else {
          toast.success(data.message);
        }
        
        setInviteForm({ email: "", name: "", invite_type: "email", role: "community_member", tier: "basic" });
        fetchInvitations();
        fetchCommunityMembers();
        setShowInviteDialog(false);
      } else {
        const errorData = await response.json();
        console.error("Error response:", errorData);
        toast.error(errorData.detail || "Failed to send invite");
      }
    } catch (_err) {
      console.error("Network error:", _err);
      console.error("Error name:", _err.name);
      console.error("Error message:", _err.message);
      toast.error(`Unable to connect: ${_err.message}`);
    } finally {
      setSendingInvite(false);
    }
  };

  // Send intake form to a potential client
  const handleSendIntake = async () => {
    if (!inviteForm.email) {
      toast.error("Email is required");
      return;
    }
    setSendingIntake(true);
    try {
      const response = await authFetch(`${API}/intake/send`, {
        method: "POST",
        body: JSON.stringify({
          client_email: inviteForm.email,
          client_name: inviteForm.name || "Potential Client",
          custom_message: inviteForm.intake_message || null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Intake form sent to ${inviteForm.email}`);
        setInviteForm({ email: "", name: "", invite_type: "email", role: "community_member", tier: "basic", send_intake: false, intake_message: "" });
        setShowInviteDialog(false);
        fetchData(); // Refresh to show the new pending intake
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to send intake form");
      }
    } catch (_err) {
      console.error("Send intake error:", _err);
      toast.error("Unable to connect. Please try again.");
    } finally {
      setSendingIntake(false);
    }
  };

  // Handler for sending intake to existing client
  const handleSendIntakeToExistingClient = async () => {
    if (!sendIntakeClient) return;
    
    setSendingIntakeToClient(true);
    try {
      const response = await authFetch(`${API}/intake/send`, {
        method: "POST",
        body: JSON.stringify({
          client_email: sendIntakeClient.email,
          client_name: sendIntakeClient.name || "Client",
          custom_message: intakeMessage || null
        })
      });
      
      // Check status first
      const status = response.status;
      const ok = response.ok;
      
      // Then read body
      let responseData;
      try {
        responseData = await response.json();
      } catch {
        responseData = { detail: "Server error" };
      }
      
      if (ok) {
        // Check if email was actually sent or if we need to show the URL
        if (responseData.email_sent === false && responseData.intake_url) {
          // Email failed but intake was created - show URL to copy
          toast.info(
            <div>
              <p>Intake created but email could not be sent.</p>
              <p className="mt-2 text-xs">Copy this link to share manually:</p>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(responseData.intake_url);
                  toast.success("Link copied!");
                }}
                className="mt-1 text-blue-500 underline text-xs"
              >
                Click to copy intake link
              </button>
            </div>,
            { duration: 10000 }
          );
        } else {
          toast.success(`Intake form sent to ${sendIntakeClient.name}`);
        }
        setSendIntakeClient(null);
        setIntakeMessage("");
        fetchData();
      } else {
        const errorMessage = responseData?.detail || `Error ${status}: Failed to send intake form`;
        console.error("Intake send error:", status, responseData);
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error("Send intake to client error:", err);
      toast.error(`Connection error: ${err.message || "Please try again."}`);
    } finally {
      setSendingIntakeToClient(false);
    }
  };


  const handleCancelInvite = async (inviteId) => {
    try {
      const response = await authFetch(`${API}/consultant/invitations/${inviteId}`, {
        method: "DELETE"
      });
      if (response.ok) {
        toast.success("Invitation cancelled");
        fetchInvitations();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || "Failed to cancel invitation");
      }
    } catch (_err) {
      console.error("Cancel invite error:", _err);
      toast.error("Unable to connect. Please try again.");
    }
  };

  // Handler for cancelling invites from the old client_invites system
  const handleCancelPendingInvite = async (inviteId) => {
    try {
      const response = await authFetch(`${API}/consultant/invites/${inviteId}`, {
        method: "DELETE"
      });
      if (response.ok) {
        toast.success("Invitation cancelled");
        fetchPendingInvites();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || "Failed to cancel invitation");
      }
    } catch (_err) {
      console.error("Cancel pending invite error:", _err);
      toast.error("Unable to connect. Please try again.");
    }
  };

  const handleClearAllInvitations = async () => {
    if (!confirm("Are you sure you want to clear all pending invitations?")) {
      return;
    }
    try {
      const response = await authFetch(`${API}/consultant/invitations`, {
        method: "DELETE"
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "All invitations cleared");
        fetchInvitations();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || "Failed to clear invitations");
      }
    } catch (_err) {
      console.error("Clear all invites error:", _err);
      toast.error("Unable to connect. Please try again.");
    }
  };

  // Handler for clearing all old client_invites
  const handleClearAllPendingInvites = async () => {
    if (!confirm("Are you sure you want to clear all pending invitations?")) {
      return;
    }
    try {
      // Clear from both collections to ensure all pending invites are removed
      const [res1, res2] = await Promise.all([
        authFetch(`${API}/consultant/invitations`, { method: "DELETE" }),
        authFetch(`${API}/consultant/invites`, { method: "DELETE" }),
      ]);
      const d1 = res1.ok ? await res1.json() : {};
      const d2 = res2.ok ? await res2.json() : {};
      toast.success(`Cleared all pending invitations`);
      fetchPendingInvites();
      fetchData();
    } catch (_err) {
      console.error("Clear all pending invites error:", _err);
      toast.error("Unable to connect. Please try again.");
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      const response = await authFetch(`${API}/consultant/invitations/${inviteId}/resend`, {
        method: "POST"
      });
      if (response.ok) {
        toast.success("Invitation resent");
        fetchInvitations();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || "Failed to resend invitation");
      }
    } catch (_err) {
      console.error("Resend invite error:", _err);
      toast.error("Unable to connect. Please try again.");
    }
  };

  // Event functions
  const fetchEvents = async () => {
    try {
      const res = await authFetch(`${API}/consultant/events`, {  });
      if (res.ok) setEvents(await res.json());
    } catch (_error) {
      console.error("Error fetching events:", _error);
    }
  };

  const handleCreateEvent = async () => {
    if (!eventForm.title || !eventForm.date || !eventForm.time) {
      toast.error("Title, date, and time are required");
      return;
    }
    setSavingEvent(true);
    try {
      const response = await authFetch(`${API}/consultant/events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(eventForm)
      });
      if (response.ok) {
        toast.success("Event created!");
        setEventForm({ title: "", description: "", date: "", time: "", duration: "60", location: "Online", meeting_link: "", payment_link: "", price: 0, max_attendees: 15 });
        fetchEvents();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create event");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSavingEvent(false);
    }
  };

  const handleUpdateEvent = async () => {
    if (!editingEvent) return;
    setSavingEvent(true);
    try {
      const response = await authFetch(`${API}/consultant/events/${editingEvent.workshop_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(eventForm)
      });
      if (response.ok) {
        toast.success("Event updated!");
        setEditingEvent(null);
        setEventForm({ title: "", description: "", date: "", time: "", duration: "60", location: "Online", meeting_link: "", payment_link: "", price: 0, max_attendees: 15 });
        fetchEvents();
      }
    } catch (_error) {
      toast.error("Failed to update event");
    } finally {
      setSavingEvent(false);
    }
  };

  const handleDeleteEvent = async (workshopId) => {
    if (!confirm("Delete this event?")) return;
    try {
      const response = await authFetch(`${API}/consultant/events/${workshopId}`, {
        method: "DELETE",
        
      });
      if (response.ok) {
        toast.success("Event deleted");
        fetchEvents();
      }
    } catch (_error) {
      toast.error("Failed to delete event");
    }
  };

  const openEditEventDialog = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title || "",
      description: event.description || "",
      date: event.date || "",
      time: event.time || "",
      duration: event.duration || "60",
      location: event.location || "Online",
      meeting_link: event.meeting_link || "",
      payment_link: event.payment_link || "",
      price: event.price || 0,
      max_attendees: event.max_attendees || 15
    });
  };

  const fetchEventAttendees = async (workshopId) => {
    try {
      const res = await authFetch(`${API}/consultant/events/${workshopId}/registrations`, {  });
      if (res.ok) {
        const data = await res.json();
        setSelectedEventAttendees(data);
      }
    } catch (_error) {
      toast.error("Failed to fetch attendees");
    }
  };

  const handleSendReminder = async (workshopId) => {
    setSendingReminder(true);
    try {
      const response = await authFetch(`${API}/consultant/events/${workshopId}/send-reminder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ message: reminderMessage })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setReminderMessage("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send reminders");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSendingReminder(false);
    }
  };

  const handleRemoveAttendee = async (workshopId, userId) => {
    if (!confirm("Remove this attendee?")) return;
    try {
      const response = await authFetch(`${API}/consultant/events/${workshopId}/remove-attendee`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ user_id: userId })
      });
      if (response.ok) {
        toast.success("Attendee removed");
        fetchEventAttendees(workshopId);
        fetchEvents();
      }
    } catch (_error) {
      toast.error("Failed to remove attendee");
    }
  };

  // Community Members functions
  const fetchCommunityMembers = async () => {
    try {
      const res = await authFetch(`${API}/consultant/community-members`, {  });
      if (res.ok) setCommunityMembers(await res.json());
    } catch (_error) {
      console.error("Error fetching community members:", _error);
    }
  };

  // Fetch pending recommendations count for sidebar badge
  const fetchPendingRecommendationsCount = async () => {
    try {
      const res = await authFetch(`${API}/community/consultant/recommendations?status=pending`);
      if (res.ok) {
        const data = await res.json();
        setPendingRecommendationsCount(data.length || 0);
      }
    } catch (error) {
      console.error("Error fetching pending recommendations:", error);
    }
  };

  const handleUpgradeCommunityMember = async (userId, tier) => {
    setUpgradingMember(userId);
    try {
      const response = await authFetch(`${API}/consultant/community-members/${userId}/upgrade`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ tier })
      });
      if (response.ok) {
        toast.success("Member upgraded successfully!");
        fetchCommunityMembers();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to upgrade member");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setUpgradingMember(null);
    }
  };

  const handleDeleteMember = async (userId) => {
    try {
      const response = await authFetch(`${API}/consultant/members/${userId}`, { method: "DELETE" });
      if (response.ok) {
        toast.success("Member removed from portal");
        fetchCommunityMembers();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete member");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  // Newsletter functions
  const fetchNewsletters = async () => {
    try {
      const res = await authFetch(`${API}/consultant/newsletters`, {  });
      if (res.ok) setNewsletters(await res.json());
    } catch (_error) {
      console.error("Error fetching newsletters:", _error);
    }
  };

  const fetchReferrals = async () => {
    try {
      const res = await authFetch(`${API}/referrals/all`, {  });
      if (res.ok) {
        const data = await res.json();
        setReferrals(data.referrals || []);
        setReferralStats(data.stats || { total: 0, pending: 0, approved: 0 });
      }
    } catch (_error) {
      console.error("Error fetching referrals:", _error);
    }
  };

  const handleApproveReferral = async (referralId) => {
    setProcessingReferral(referralId);
    try {
      const response = await authFetch(`${API}/referrals/${referralId}/approve`, {
        method: "POST"
      });
      if (response.ok) {
        toast.success("Referral approved! Reward coupon sent to referrer.");
        fetchReferrals();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to approve referral");
      }
    } catch (_error) {
      toast.error("Failed to approve referral");
    } finally {
      setProcessingReferral(null);
    }
  };

  const handleUpdateReferralStatus = async (referralId, status) => {
    setProcessingReferral(referralId);
    try {
      const response = await authFetch(`${API}/referrals/${referralId}/status`, {
        method: "POST",
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        toast.success(`Status updated to ${status}`);
        fetchReferrals();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to update status");
      }
    } catch (_error) {
      toast.error("Failed to update status");
    } finally {
      setProcessingReferral(null);
    }
  };

  const fetchNewsletterSettings = async () => {
    try {
      const res = await authFetch(`${API}/consultant/newsletter/settings`, {  });
      if (res.ok) setNewsletterSettings(await res.json());
    } catch (_error) {
      console.error("Error fetching newsletter settings:", _error);
    }
  };

  const handleGenerateNewsletter = async () => {
    setGeneratingNewsletter(true);
    try {
      const response = await authFetch(`${API}/consultant/newsletter/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          focus_topics: newsletterSettings.default_topics,
          include_workshops: newsletterSettings.include_workshops
        })
      });
      if (response.ok) {
        const data = await response.json();
        setNewsletterContent({ subject: data.subject, content: data.content });
        toast.success("Newsletter draft generated!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to generate newsletter");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setGeneratingNewsletter(false);
    }
  };

  const handleSendNewsletter = async () => {
    if (!newsletterContent.subject || !newsletterContent.content) {
      toast.error("Please generate or write newsletter content first");
      return;
    }
    setSendingNewsletter(true);
    try {
      const response = await authFetch(`${API}/consultant/newsletter/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(newsletterContent)
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Newsletter sent to ${data.sent_to} recipients!`);
        setShowNewsletterDialog(false);
        setNewsletterContent({ subject: "", content: "" });
        fetchNewsletters();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send newsletter");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSendingNewsletter(false);
    }
  };

  const handleSaveNewsletterSettings = async () => {
    try {
      const response = await authFetch(`${API}/consultant/newsletter/settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(newsletterSettings)
      });
      if (response.ok) {
        toast.success("Newsletter settings saved!");
      } else {
        toast.error("Failed to save settings");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  // Community View - Comment functions
  const fetchPendingComments = async () => {
    try {
      const res = await authFetch(`${API}/consultant/pending-comments`, {  });
      if (res.ok) setPendingComments(await res.json());
    } catch (_error) {
      console.error("Error fetching pending comments:", _error);
    }
  };

  const fetchPostComments = async (postId) => {
    setLoadingComments(prev => ({ ...prev, [postId]: true }));
    try {
      const res = await authFetch(`${API}/bulletin/posts/${postId}/comments`, {  });
      if (res.ok) {
        const comments = await res.json();
        setPostComments(prev => ({ ...prev, [postId]: comments }));
      }
    } catch (_error) {
      console.error("Error fetching comments:", _error);
    } finally {
      setLoadingComments(prev => ({ ...prev, [postId]: false }));
    }
  };

  const handleApproveComment = async (postId, commentId) => {
    try {
      const response = await authFetch(`${API}/bulletin/posts/${postId}/comments/${commentId}/approve`, {
        method: "PUT",
        
      });
      if (response.ok) {
        toast.success("Comment approved!");
        fetchPendingComments();
        fetchPostComments(postId);
        fetchBulletinPosts();
      } else {
        toast.error("Failed to approve comment");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const handleRejectComment = async (postId, commentId) => {
    try {
      const response = await authFetch(`${API}/bulletin/posts/${postId}/comments/${commentId}/reject`, {
        method: "PUT",
        
      });
      if (response.ok) {
        toast.success("Comment rejected");
        fetchPendingComments();
        fetchPostComments(postId);
      } else {
        toast.error("Failed to reject comment");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const handleDeleteComment = async (postId, commentId) => {
    if (!confirm("Delete this comment?")) return;
    try {
      const response = await authFetch(`${API}/bulletin/posts/${postId}/comments/${commentId}`, {
        method: "DELETE",
        
      });
      if (response.ok) {
        toast.success("Comment deleted");
        fetchPostComments(postId);
        fetchBulletinPosts();
      } else {
        toast.error("Failed to delete comment");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const handleAddComment = async (postId) => {
    const content = newComment[postId]?.trim();
    if (!content) return;
    
    setSubmittingComment(true);
    try {
      const response = await authFetch(`${API}/bulletin/posts/${postId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ content })
      });
      if (response.ok) {
        toast.success("Comment added!");
        setNewComment(prev => ({ ...prev, [postId]: "" }));
        fetchPostComments(postId);
        fetchBulletinPosts();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add comment");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSubmittingComment(false);
    }
  };

  const togglePostExpand = (postId) => {
    const isExpanding = !expandedPosts[postId];
    setExpandedPosts(prev => ({ ...prev, [postId]: isExpanding }));
    if (isExpanding && !postComments[postId]) {
      fetchPostComments(postId);
    }
  };

  const handleProcessUpgrade = async (requestId, action) => {
    setProcessingUpgrade(requestId);
    try {
      const response = await authFetch(`${API}/consultant/upgrade-requests/${requestId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ action })
      });
      if (response.ok) {
        toast.success(`Upgrade request ${action}d successfully!`);
        fetchUpgradeRequests();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || `Failed to ${action} request`);
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setProcessingUpgrade(null);
    }
  };

  const handleApproveClient = async (clientId, program) => {
    setApprovingClient(clientId);
    try {
      const response = await authFetch(`${API}/consultant/clients/${clientId}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ program })
      });
      if (response.ok) {
        toast.success("Client approved successfully!");
        fetchPendingClients();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to approve client");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setApprovingClient(null);
    }
  };

  const handleSuspendClient = async (clientId) => {
    setProcessingAction(clientId);
    try {
      const response = await authFetch(`${API}/consultant/clients/${clientId}/suspend`, {
        method: "POST",
        
      });
      if (response.ok) {
        toast.success("Client access suspended");
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to suspend client");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setProcessingAction(null);
    }
  };

  const handleManualOnboard = async (clientId, clientName) => {
    if (!confirm(`Manually onboard ${clientName}? This will mark them as active and grant full access.`)) {
      return;
    }
    setProcessingAction(clientId);
    try {
      const response = await authFetch(`${API}/consultant/clients/${clientId}/manual-onboard`, {
        method: "POST",
      });
      if (response.ok) {
        toast.success(`${clientName} has been onboarded successfully`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to onboard client");
      }
    } catch (_error) {
      toast.error("An error occurred while onboarding");
    } finally {
      setProcessingAction(null);
    }
  };

  const handleGenerateSponsorReport = async (clientId, clientName) => {
    setGeneratingReport(clientId);
    try {
      const response = await authFetch(`${API}/consultant/clients/${clientId}/sponsor-report`, {});
      if (response.ok) {
        const data = await response.json();
        // Create download link
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${data.pdf_base64}`;
        link.download = data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`Sponsor report generated for ${clientName}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to generate report");
      }
    } catch (_error) {
      toast.error("Failed to generate sponsor report");
    } finally {
      setGeneratingReport(null);
    }
  };

  const handleRestoreClient = async (clientId) => {
    setProcessingAction(clientId);
    try {
      const response = await authFetch(`${API}/consultant/clients/${clientId}/restore`, {
        method: "POST",
        
      });
      if (response.ok) {
        toast.success("Client access restored");
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to restore client");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setProcessingAction(null);
    }
  };

  const handleDeleteClient = async () => {
    if (!clientToDelete) return;
    setProcessingAction(clientToDelete.user_id);
    try {
      const response = await authFetch(`${API}/consultant/clients/${clientToDelete.user_id}`, {
        method: "DELETE",
        
      });
      if (response.ok) {
        toast.success("Client permanently deleted");
        setShowDeleteClientDialog(false);
        setClientToDelete(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete client");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setProcessingAction(null);
    }
  };

  const handleDeleteConsultant = async () => {
    if (!consultantToDelete) return;
    setProcessingAction(consultantToDelete.user_id);
    try {
      const response = await authFetch(`${API}/consultant/team/${consultantToDelete.user_id}`, {
        method: "DELETE",
        
      });
      if (response.ok) {
        toast.success("Consultant removed");
        setShowDeleteConsultantDialog(false);
        setConsultantToDelete(null);
        fetchAllConsultants();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to remove consultant");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setProcessingAction(null);
    }
  };

  const handleAssignConsultant = async (clientId, consultantId) => {
    try {
      const response = await authFetch(`${API}/consultant/clients/${clientId}/assign-consultant`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ consultant_id: consultantId })
      });
      if (response.ok) {
        toast.success("Consultant assigned!");
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to assign consultant");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const handleUnassignConsultant = async (clientId, consultantId) => {
    try {
      const response = await authFetch(`${API}/consultant/clients/${clientId}/unassign-consultant/${consultantId}`, {
        method: "DELETE",
        
      });
      if (response.ok) {
        toast.success("Consultant unassigned");
        fetchData();
      } else {
        toast.error("Failed to unassign consultant");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const handleInviteClient = async () => {
    if (!addClientForm.name || !addClientForm.email) {
      toast.error("Name and email are required");
      return;
    }
    
    setSendingInvite(true);
    try {
      const response = await authFetch(`${API}/consultant/invite-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: addClientForm.email,
          name: addClientForm.name,
          role: "client",
          tier: addClientForm.tier || "basic",
          invite_type: addClientForm.invite_type || "email",
          intake_message: addClientForm.intake_message
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || `Invitation sent to ${addClientForm.email}`);
        setShowAddClientDialog(false);
        setAddClientForm({ name: "", email: "", tier: "basic", invite_type: "email", intake_message: "" });
        // Refresh data in background without blocking
        fetchPendingInvites().catch(() => {});
        fetchPendingClients().catch(() => {});
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send invitation");
      }
    } catch (_error) {
      console.error("Invite _error:", _error);
      toast.error("Failed to send invitation");
    } finally {
      setSendingInvite(false);
    }
  };

  // Send intake form to a client from the Add Client dialog
  const handleSendClientIntake = async () => {
    if (!addClientForm.name || !addClientForm.email) {
      toast.error("Name and email are required");
      return;
    }
    
    setSendingIntake(true);
    try {
      const response = await authFetch(`${API}/intake/send`, {
        method: "POST",
        body: JSON.stringify({
          client_email: addClientForm.email,
          client_name: addClientForm.name,
          custom_message: addClientForm.intake_message || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Intake form sent to ${addClientForm.email}`);
        setShowAddClientDialog(false);
        setAddClientForm({ name: "", email: "", tier: "basic", invite_type: "email", intake_message: "" });
        fetchData(); // Refresh to show updated data
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send intake form");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSendingIntake(false);
    }
  };

  const handleSendReminderOld = async (inviteId) => {
    setSendingReminder(inviteId);
    try {
      const response = await authFetch(`${API}/consultant/invites/${inviteId}/remind`, {
        method: "POST",
        
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchPendingInvites();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send reminder");
      }
    } catch (_error) {
      toast.error("Failed to send reminder");
    } finally {
      setSendingReminder(null);
    }
  };

  const handleUpdateClientTier = async (clientId, newTier) => {
    try {
      const response = await authFetch(`${API}/consultant/clients/${clientId}/tier`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ tier: newTier })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        // Update local state
        setClients(clients.map(c => 
          c.user_id === clientId ? { ...c, tier: newTier } : c
        ));
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update tier");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const getTierBadgeStyle = (tier) => {
    switch(tier) {
      case "basic":
        return "bg-blue-100 text-blue-700 border-blue-200";
      case "premium_rapid":
        return "bg-purple-100 text-purple-700 border-purple-200";
      case "podcast_academy":
        return "bg-pink-100 text-pink-700 border-pink-200";
      case "growth_strategic":
        return "bg-[#39FF14]/10 text-[#39FF14] border-[#39FF14]/30";
      case "growth_optimization":
        return "bg-amber-100 text-amber-700 border-amber-200";
      case "growth_scale":
        return "bg-rose-100 text-rose-700 border-rose-200";
      default:
        return "bg-gray-100 text-gray-600 border-gray-200";
    }
  };

  const getTierLabel = (tier) => {
    switch(tier) {
      case "basic": return "Basic";
      case "premium_rapid": return "R.A.P.I.D.™";
      case "podcast_academy": return "Podcast Academy";
      case "growth_strategic": return "Strategic";
      case "growth_optimization": return "Growth";
      case "growth_scale": return "Scale";
      default: return "Unassigned";
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleAddConsultant = async () => {
    if (!consultantForm.name || !consultantForm.email || !consultantForm.password) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const response = await authFetch(`${API}/consultant/add-consultant`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(consultantForm)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Consultant "${data.consultant.name}" added successfully!`);
        setShowAddConsultantDialog(false);
        setConsultantForm({ name: "", email: "", password: "" });
        fetchData(); // Refresh team list
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add consultant");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const handleCreateWorkshop = async () => {
    try {
      const response = await authFetch(`${API}/consultant/workshops`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(workshopForm)
      });

      if (response.ok) {
        toast.success("Event created successfully!");
        setShowWorkshopDialog(false);
        setWorkshopForm({
          title: "",
          description: "",
          date: "",
          time: "",
          duration_minutes: 60,
          type: "workshop",
          meeting_link: "",
          payment_link: "",
          location: "Online"
        });
        fetchEvents();  // Refresh events after creation
        fetchData();
      } else {
        toast.error("Failed to create event");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const handleScheduleMeeting = async () => {
    try {
      const response = await authFetch(`${API}/consultant/meetings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(meetingForm)
      });

      if (response.ok) {
        toast.success("Meeting scheduled and client notified via email!");
        setShowMeetingDialog(false);
        setMeetingForm({
          client_id: "",
          title: "",
          description: "",
          date: "",
          time: "",
          duration_minutes: 60,
          meeting_type: "strategy"
        });
        fetchData();
      } else {
        toast.error("Failed to schedule meeting");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };


  const handleUpdateMeetingStatus = async (meetingId, status, reason) => {
    try {
      const res = await authFetch(`${API}/consultant/meetings/${meetingId}`, {
        method: "PUT",
        body: JSON.stringify({ status, reason: reason || "" }),
      });
      if (res.ok) {
        toast.success(`Meeting ${status.replace("_", " ")}`);
        fetchData();
      } else {
        toast.error("Failed to update meeting");
      }
    } catch (_e) {
      toast.error("Failed to update meeting");
    }
  };

  const handleSaveSettings = async () => {
    try {
      const response = await authFetch(`${API}/consultant/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success("Settings saved!");
        setShowSettingsDialog(false);
        checkAuth();
      } else {
        toast.error("Failed to save settings");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const handleSendReminders = async () => {
    try {
      const response = await authFetch(`${API}/send-weekly-reminders`, {
        method: "POST",
        
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
      } else {
        toast.error("Failed to send reminders");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const filteredClients = clients.filter(client => 
    client.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const onboardedClients = clients.filter(c => c.onboarded);
  const notOnboardedClients = clients.filter(c => !c.onboarded);
  const upcomingMeetings = meetings.filter(m => m.status !== "completed" && m.status !== "cancelled");

  // Stats for condensed sidebar
  const sidebarStats = {
    totalClients: clients.length,
    pendingClients: pendingClients.length,
    leads: assessmentLeads.length,
    scaleApps: 0, // Could add scale applications count if needed
    events: events.length,
    community: communityMembers.stats?.total_community_access || 0,
    referrals: referralStats.pending || 0,
    recommendations: pendingRecommendationsCount
  };

  // Navigation handler for condensed sidebar
  const handleSidebarNavigate = (section) => {
    switch (section) {
      case 'overview':
        // Already on overview
        break;
      case 'projects':
        navigate('/consultant/projects');
        break;
      case 'addClient':
        setShowAddClientDialog(true);
        break;
      case 'inviteCommunity':
        setShowCommunityDialog(true);
        break;
      case 'meeting':
        setShowMeetingDialog(true);
        break;
      case 'bulletin':
        setEditingPost(null);
        setBulletinForm({ title: "", content: "", category: "announcement", link_url: "", link_text: "" });
        setShowBulletinDialog(true);
        break;
      case 'pipeline':
        setClientViewMode('pipeline');
        break;
      case 'leads':
        setShowLeadsPanel(true);
        break;
      case 'scaleApps':
        setShowScaleApplications(true);
        break;
      case 'cases':
        setShowCasesView(true);
        break;
      case 'rapid':
        navigate('/rapid');
        break;
      case 'podcast':
        navigate('/podcast/manage');
        break;
      case 'academyTraining':
        navigate('/consultant/podcast-academy');
        break;
      case 'scaling':
        navigate('/scaling');
        break;
      case 'recommendations':
        setShowRecommendations(true);
        break;
      case 'newsletters':
        setShowNewsletterDialog(true);
        break;
      case 'events':
        setShowEventsDialog(true);
        break;
      case 'blog':
        navigate('/consultant/blog');
        break;
      case 'communityHub':
        navigate('/community');
        break;
      case 'communityManagement':
        setShowCommunityDialog(true);
        break;
      case 'community':
        setShowCommunityView(true);
        break;
      case 'referrals':
        setShowReferralsDialog(true);
        break;
      case 'analytics':
        navigate('/consultant/analytics');
        break;
      case 'payments':
        navigate('/consultant/payments');
        break;
      case 'timeBudget':
        navigate('/consultant/time-budget');
        break;
      case 'security':
        setShowSecurityPanel(true);
        break;
      case 'sponsorship':
        setShowSponsorshipPanel(true);
        break;
      case 'serviceQuotes':
        setShowServiceQuotes(true);
        break;
      case 'sponsorPortals':
        setShowSponsorManagement(true);
        break;
      case 'companies':
        setShowCompanyManagement(true);
        break;
      case 'inviteAnalytics':
        setShowInviteAnalytics(true);
        break;
      case 'clientOnboarding':
        setShowOnboardingChecklist(true);
        break;
      case 'clientProgress':
        setShowClientProgress(true);
        break;
      case 'progressReports':
        setShowProgressReports(true);
        break;
      case 'billing':
        setShowBilling(true);
        break;
      case 'salesAgents':
        setShowSalesAgents(true);
        break;
      case 'partnershipAgent':
        setShowPartnershipAgent(true);
        break;
      case 'activityScores':
        setShowActivityScores(true);
        break;
      case 'proposals':
        setShowProposals(true);
        break;
      case 'agentManagement':
        setShowAgentMgmt(true);
        break;
      case 'formationOrders':
        setShowFormationOrders(true);
        break;
      case 'scheduleMeeting':
        setShowMeetingDialog(true);
        break;
      case 'addClient':
        setShowAddClientDialog(true);
        break;
      case 'milesAgent':
        setShowMilesPanel(true);
        break;
      case 'outreach':
        setShowOutreachPanel(true);
        break;
      case 'onboarding':
        navigate('/consultant/onboarding');
        break;
      case 'help':
        navigate('/help');
        break;
      case 'settings':
        setShowSettingsDialog(true);
        break;
      case 'pricing':
        navigate('/pricing');
        break;
      default:
        break;
    }
  };

  // Process pending quick action after handleSidebarNavigate is available
  useEffect(() => {
    if (pendingQuickAction) {
      handleSidebarNavigate(pendingQuickAction);
      setPendingQuickAction(null);
    }
  }, [pendingQuickAction]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#333333] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="consultant-dashboard">
      {/* Condensed Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 hidden lg:block">
        <CondensedSidebar
          user={user}
          isAdmin={isAdmin}
          activeSection="overview"
          stats={sidebarStats}
          onNavigate={handleSidebarNavigate}
          onLogout={handleLogout}
        />
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-[#39FF14] rounded-md flex items-center justify-center">
              <span className="text-white font-bold text-sm">MB</span>
            </div>
            <span className="font-semibold text-neutral-900">Consultant</span>
          </div>
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 bg-neutral-900 text-white p-0">
              <CondensedSidebar
                user={user}
                isAdmin={isAdmin}
                activeSection="overview"
                stats={sidebarStats}
                onNavigate={(section) => {
                  handleSidebarNavigate(section);
                  setMobileMenuOpen(false);
                }}
                onLogout={handleLogout}
              />
            </SheetContent>
          </Sheet>
        </div>

        {/* Success Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg z-50 animate-in slide-in-from-right">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-green-800 font-medium">{notification}</p>
              <button onClick={() => setNotification(null)} className="ml-4 text-green-600 hover:text-green-800">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Admin Panel */}
        <AdminPanel user={user} />

        {/* Header - Extracted Component */}
        <DashboardHeader
          user={user}
          onSendReminders={handleSendReminders}
          showMeetingDialog={showMeetingDialog}
          setShowMeetingDialog={setShowMeetingDialog}
          meetingForm={meetingForm}
          setMeetingForm={setMeetingForm}
          onboardedClients={onboardedClients}
          onScheduleMeeting={handleScheduleMeeting}
          showWorkshopDialog={showWorkshopDialog}
          setShowWorkshopDialog={setShowWorkshopDialog}
          workshopForm={workshopForm}
          setWorkshopForm={setWorkshopForm}
          onCreateWorkshop={handleCreateWorkshop}
        />

        {/* Notification Bell + Quick Actions */}
        <div className="flex items-center justify-between mb-2">
          <QuickActionsBar onNavigate={handleSidebarNavigate} />
          <NotificationBell />
        </div>

        {/* Stats */}
        <DashboardStats 
          clients={clients}
          onboardedClients={onboardedClients}
          pendingClients={pendingClients}
          upcomingMeetings={upcomingMeetings}
          handleUpdateMeetingStatus={handleUpdateMeetingStatus}
          events={events}
        />

        {/* Client Pipeline or List View */}
        {clientViewMode === 'pipeline' ? (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Button
                  variant={clientViewMode === 'pipeline' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setClientViewMode('pipeline')}
                  className={clientViewMode === 'pipeline' ? 'bg-neutral-900' : ''}
                >
                  <Target className="w-4 h-4 mr-1" />
                  Pipeline
                </Button>
                <Button
                  variant={clientViewMode === 'list' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setClientViewMode('list')}
                  className={clientViewMode === 'list' ? 'bg-neutral-900' : ''}
                >
                  <Users className="w-4 h-4 mr-1" />
                  List
                </Button>
              </div>
              <Dialog open={showAddClientDialog} onOpenChange={setShowAddClientDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-[#39FF14] hover:bg-[#32E010]" data-testid="add-client-btn">
                    <UserPlus className="w-4 h-4 mr-1" />
                    Add Client
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Invite New Client</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Invitation Type</Label>
                      <Select
                        value={addClientForm.invite_type}
                        onValueChange={(value) => setAddClientForm({ ...addClientForm, invite_type: value })}
                      >
                        <SelectTrigger data-testid="add-client-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full_client">Full Client</SelectItem>
                          <SelectItem value="intake_only">Intake Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="newClientName">Name</Label>
                      <Input id="newClientName" value={addClientForm.name} onChange={(e) => setAddClientForm({...addClientForm, name: e.target.value})} placeholder="Client name" data-testid="add-client-name-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="newClientEmail">Email</Label>
                      <Input id="newClientEmail" type="email" value={addClientForm.email} onChange={(e) => setAddClientForm({...addClientForm, email: e.target.value})} placeholder="client@example.com" data-testid="add-client-email-input" />
                    </div>
                    <Button onClick={handleInviteClient} disabled={sendingInvite} className="w-full bg-[#39FF14] hover:bg-[#32E010]" data-testid="add-client-submit-btn">
                      {sendingInvite ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                      Send Invitation
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <ClientPipeline
              clients={clients}
              leads={assessmentLeads}
              onViewClient={(userId) => navigate(`/consultant/client/${userId}`)}
              onManagePrograms={(client) => { setSelectedClientForPrograms(client); setShowProgramsDialog(true); }}
              onSendIntake={(client) => setSendIntakeClient(client)}
              onGenerateReport={handleGenerateSponsorReport}
              onAssignConsultant={(client) => { setSelectedClientForAssign(client); setShowAssignDialog(true); }}
              onManualOnboard={handleManualOnboard}
              onSuspend={handleSuspendClient}
              onRestore={handleRestoreClient}
              onDelete={(client) => { setClientToDelete(client); setShowDeleteClientDialog(true); }}
              onUpdateTier={handleUpdateClientTier}
              getTierLabel={getTierLabel}
              getTierBadgeStyle={getTierBadgeStyle}
              processingAction={processingAction}
              generatingReport={generatingReport}
            />
          </div>
        ) : (
          <ClientListView
            user={user}
            clients={clients}
            filteredClients={filteredClients}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onboardedClients={onboardedClients}
            pendingInvites={pendingInvites}
            pendingClients={pendingClients}
            upgradeRequests={upgradeRequests}
            pendingIntakes={pendingIntakes}
            upcomingMeetings={upcomingMeetings}
            events={events}
            teamMembers={teamMembers}
            showAddClientDialog={showAddClientDialog}
            setShowAddClientDialog={setShowAddClientDialog}
            addClientForm={addClientForm}
            setAddClientForm={setAddClientForm}
            sendingInvite={sendingInvite}
            sendingIntake={sendingIntake}
            handleInviteClient={handleInviteClient}
            handleSendClientIntake={handleSendClientIntake}
            handleSendReminderOld={handleSendReminderOld}
            sendingReminder={sendingReminder}
            handleCancelPendingInvite={handleCancelPendingInvite}
            handleClearAllPendingInvites={handleClearAllPendingInvites}
            handleApproveClient={handleApproveClient}
            approvingClient={approvingClient}
            handleProcessUpgrade={handleProcessUpgrade}
            processingUpgrade={processingUpgrade}
            handleApproveIntake={handleApproveIntake}
            handleDenyIntake={handleDenyIntake}
            processingIntake={processingIntake}
            handleUpdateClientTier={handleUpdateClientTier}
            handleManualOnboard={handleManualOnboard}
            handleSuspendClient={handleSuspendClient}
            handleRestoreClient={handleRestoreClient}
            handleGenerateSponsorReport={handleGenerateSponsorReport}
            generatingReport={generatingReport}
            processingAction={processingAction}
            setSelectedClientForPrograms={setSelectedClientForPrograms}
            setShowProgramsDialog={setShowProgramsDialog}
            setSendIntakeClient={setSendIntakeClient}
            setSelectedClientForAssign={setSelectedClientForAssign}
            setShowAssignDialog={setShowAssignDialog}
            setClientToDelete={setClientToDelete}
            setShowDeleteClientDialog={setShowDeleteClientDialog}
            getTierLabel={getTierLabel}
            getTierBadgeStyle={getTierBadgeStyle}
            setClientViewMode={setClientViewMode}
            setShowMeetingDialog={setShowMeetingDialog}
            setShowWorkshopDialog={setShowWorkshopDialog}
            setShowSettingsDialog={setShowSettingsDialog}
            setShowAddConsultantDialog={setShowAddConsultantDialog}
            setConsultantToDelete={setConsultantToDelete}
            setShowDeleteConsultantDialog={setShowDeleteConsultantDialog}
            setShowEventsDialog={setShowEventsDialog}
          />
        )}

        {/* Team Management Dialogs */}
        <AddConsultantDialog
          open={showAddConsultantDialog} onOpenChange={setShowAddConsultantDialog}
          form={consultantForm} setForm={setConsultantForm} onSubmit={handleAddConsultant}
        />
        <AssignConsultantDialog
          open={showAssignDialog} onOpenChange={setShowAssignDialog}
          client={selectedClientForAssign} allConsultants={allConsultants}
          onAssign={handleAssignConsultant} onUnassign={handleUnassignConsultant}
          setClient={setSelectedClientForAssign}
        />
        <DeleteClientDialog
          open={showDeleteClientDialog} onOpenChange={setShowDeleteClientDialog}
          client={clientToDelete} onDelete={handleDeleteClient} processingAction={processingAction}
        />
        <DeleteConsultantDialog
          open={showDeleteConsultantDialog} onOpenChange={setShowDeleteConsultantDialog}
          consultant={consultantToDelete} onDelete={handleDeleteConsultant} processingAction={processingAction}
        />

        {/* Bulletin Board Dialog */}
        <BulletinDialog
          open={showBulletinDialog}
          onOpenChange={setShowBulletinDialog}
          bulletinPosts={bulletinPosts}
          autoBulletinSettings={autoBulletinSettings}
          onRefresh={fetchBulletinPosts}
        />

        {/* Community Members Dialog */}
        <CommunityMembersDialog
          open={showCommunityDialog}
          onOpenChange={setShowCommunityDialog}
          communityMembers={communityMembers}
          invitations={invitations}
          onInviteUser={() => { setShowCommunityDialog(false); setShowInviteDialog(true); }}
          onUpgradeMember={handleUpgradeCommunityMember}
          upgradingMember={upgradingMember}
          onDeleteMember={handleDeleteMember}
        />

        {/* Newsletter Dialog */}
        <NewsletterDialog
          open={showNewsletterDialog}
          onOpenChange={setShowNewsletterDialog}
          newsletters={newsletters}
          recipientCount={communityMembers.stats?.total_community_access || 0}
          initialSettings={newsletterSettings}
          onRefresh={fetchNewsletters}
        />

        {/* Community View Dialog */}
        <CommunityViewDialog
          open={showCommunityView}
          onOpenChange={setShowCommunityView}
          bulletinPosts={bulletinPosts}
          pendingComments={pendingComments}
          expandedPosts={expandedPosts}
          postComments={postComments}
          loadingComments={loadingComments}
          newComment={newComment}
          setNewComment={setNewComment}
          submittingComment={submittingComment}
          onToggleExpand={togglePostExpand}
          onEditPost={(post) => { setShowCommunityView(false); openEditBulletinDialog(post); }}
          onDeletePost={handleDeleteBulletinPost}
          onApproveComment={handleApproveComment}
          onRejectComment={handleRejectComment}
          onDeleteComment={handleDeleteComment}
          onAddComment={handleAddComment}
          onCreatePost={() => { setShowCommunityView(false); setShowBulletinDialog(true); }}
        />

        {/* Invite User Dialog */}
        <InviteUserDialog
          open={showInviteDialog}
          onOpenChange={setShowInviteDialog}
          onSuccess={() => {
            fetchInvitations();
            fetchCommunityMembers();
            fetchData();
          }}
          invitations={invitations}
        />

        {/* Events Management Dialog */}
        <EventsDialog
          open={showEventsDialog}
          onOpenChange={setShowEventsDialog}
          events={events}
          onRefresh={fetchEvents}
        />

        {/* Scale Applications Panel */}
        <ScaleApplicationsPanel 
          isOpen={showScaleApplications} 
          onClose={() => setShowScaleApplications(false)} 
        />

        {/* Recommendations Panel */}
        <RecommendationsPanel
          open={showRecommendations}
          onOpenChange={setShowRecommendations}
        />

        {/* Project Scope Requests Panel */}
        <ProjectScopePanel
          open={showProjectScopes}
          onOpenChange={setShowProjectScopes}
        />

        {/* Service Quote Requests Panel */}
        <ServiceQuotePanel
          open={showServiceQuotes}
          onOpenChange={setShowServiceQuotes}
        />

        {/* Sponsor Portal Management Panel */}
        <SponsorManagementPanel
          open={showSponsorManagement}
          onOpenChange={setShowSponsorManagement}
        />

        <CompanyManagementPanel
          open={showCompanyManagement}
          onOpenChange={setShowCompanyManagement}
        />

        <InvitationAnalyticsPanel
          open={showInviteAnalytics}
          onOpenChange={setShowInviteAnalytics}
        />

        <OnboardingChecklistPanel
          open={showOnboardingChecklist}
          onOpenChange={setShowOnboardingChecklist}
        />

        <MilesAgentPanel
          open={showMilesPanel}
          onOpenChange={setShowMilesPanel}
        />

        <OutreachPanel
          open={showOutreachPanel}
          onOpenChange={setShowOutreachPanel}
        />

        {/* Referrals Management Dialog */}
        <ReferralsDialog
          open={showReferralsDialog}
          onOpenChange={setShowReferralsDialog}
          referrals={referrals}
          referralStats={referralStats}
          onRefresh={fetchReferrals}
        />

        {/* Client Programs Dialog */}
        <ClientProgramsDialog
          open={showProgramsDialog}
          onOpenChange={setShowProgramsDialog}
          client={selectedClientForPrograms}
          onRefresh={fetchData}
        />

        <ClientProgressPanel
          open={showClientProgress}
          onOpenChange={setShowClientProgress}
        />

        <ProgressReportsPanel
          open={showProgressReports}
          onOpenChange={setShowProgressReports}
        />

        <BillingDashboard
          open={showBilling}
          onOpenChange={setShowBilling}
        />

        <SalesAgentsDashboard
          open={showSalesAgents}
          onOpenChange={setShowSalesAgents}
        />

        <PartnershipAgentDashboard
          open={showPartnershipAgent}
          onOpenChange={setShowPartnershipAgent}
        />

        <ActivityScoresPanel
          open={showActivityScores}
          onOpenChange={setShowActivityScores}
        />

        <ProposalGenerator
          open={showProposals}
          onOpenChange={setShowProposals}
          clients={clients}
        />

        <AgentManagementPanel
          open={showAgentMgmt}
          onOpenChange={setShowAgentMgmt}
        />

        <FormationOrdersPanel
          open={showFormationOrders}
          onOpenChange={setShowFormationOrders}
        />

        {/* Bottom Dialogs - Extracted Component */}
        <DashboardBottomDialogs
          user={user}
          isAdmin={isAdmin}
          authFetch={authFetch}
          showSettingsDialog={showSettingsDialog}
          setShowSettingsDialog={setShowSettingsDialog}
          settings={settings}
          setSettings={setSettings}
          handleSaveSettings={handleSaveSettings}
          meetings={meetings}
          handleUpdateMeetingStatus={handleUpdateMeetingStatus}
          showCasesView={showCasesView}
          setShowCasesView={setShowCasesView}
          showLeadsPanel={showLeadsPanel}
          setShowLeadsPanel={setShowLeadsPanel}
          sendIntakeClient={sendIntakeClient}
          setSendIntakeClient={setSendIntakeClient}
          intakeMessage={intakeMessage}
          setIntakeMessage={setIntakeMessage}
          handleSendIntakeToExistingClient={handleSendIntakeToExistingClient}
          sendingIntakeToClient={sendingIntakeToClient}
          showSecurityPanel={showSecurityPanel}
          setShowSecurityPanel={setShowSecurityPanel}
          showSponsorshipPanel={showSponsorshipPanel}
          setShowSponsorshipPanel={setShowSponsorshipPanel}
        />
      </main>
    </div>
  );
};

export default ConsultantDashboard;
